import type { LibrarySong } from '../services/songMatchMemory';

export const MAX_TAG_LENGTH = 30;
export const MAX_TAGS_PER_SONG = 20;
const MAX_SUGGESTIONS = 8;

export function normalizeTag(raw: string): string | null {
  const tag = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (tag === '' || tag.length > MAX_TAG_LENGTH) return null;
  return tag;
}

export function addTag(tags: string[], raw: string): string[] | null {
  const tag = normalizeTag(raw);
  if (tag === null) return null;
  if (tags.includes(tag)) return [...tags];
  if (tags.length >= MAX_TAGS_PER_SONG) return null;
  return [...tags, tag];
}

export function removeTag(tags: string[], tag: string): string[] {
  return tags.filter(existing => existing !== tag);
}

export function tagCounts(
  tagsBySong: Map<string, string[]>,
  librarySongKeys: Set<string>,
): Array<{ tag: string; count: number }> {
  const counts = new Map<string, number>();
  for (const [songKey, tags] of tagsBySong) {
    if (!librarySongKeys.has(songKey)) continue;
    for (const tag of new Set(tags)) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => a.tag.localeCompare(b.tag));
}

export function songsWithTag(songs: LibrarySong[], tagsBySong: Map<string, string[]>, tag: string): LibrarySong[] {
  return songs.filter(song => (tagsBySong.get(song.key) ?? []).includes(tag));
}

export function suggestTags(allTags: string[], typed: string, alreadyOnSong: string[]): string[] {
  const needle = typed.trim().toLowerCase().replace(/\s+/g, ' ');
  if (needle === '') return [];
  const candidates = allTags.filter(tag => tag.includes(needle) && !alreadyOnSong.includes(tag));
  const prefixMatches = candidates.filter(tag => tag.startsWith(needle));
  const otherMatches = candidates.filter(tag => !tag.startsWith(needle));
  return [...prefixMatches, ...otherMatches].slice(0, MAX_SUGGESTIONS);
}
